package br.com.vvrefeicoes.portal.feature.users

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.Permission
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.UserAccount
import br.com.vvrefeicoes.portal.domain.model.UserImportPreview
import br.com.vvrefeicoes.portal.domain.model.UserImportResult
import br.com.vvrefeicoes.portal.domain.model.UserImportRow
import br.com.vvrefeicoes.portal.domain.model.UserProfile
import br.com.vvrefeicoes.portal.domain.repository.UserRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

enum class UserFormMode { Add, Edit }

class UsersController(
    private val repository: UserRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
) {
    private var initialized = false
    private var initializing = false
    private var totalPages = 1
    private var loadJob: Job? = null

    var loading by mutableStateOf(true)
        private set

    var busy by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var successMessage by mutableStateOf<String?>(null)
        private set

    var users by mutableStateOf<List<UserAccount>>(emptyList())
        private set

    var profiles by mutableStateOf<List<UserProfile>>(emptyList())
        private set

    var permissions by mutableStateOf<List<Permission>>(emptyList())
        private set

    var query by mutableStateOf("")
        private set

    var onlyActive by mutableStateOf(true)
        private set

    var page by mutableStateOf(0)
        private set

    val pageSize = 20

    var formMode by mutableStateOf<UserFormMode?>(null)

    var formId by mutableStateOf<String?>(null)

    var formFullName by mutableStateOf("")

    var formCargo by mutableStateOf("")

    var formSector by mutableStateOf("")

    var formPhone by mutableStateOf("")

    var formEmail by mutableStateOf("")

    var formPassword by mutableStateOf("")

    var formEditPassword by mutableStateOf("")

    var formProfileId by mutableStateOf<String?>(null)

    var formPermissions by mutableStateOf<Map<String, Set<PermissionAction>>>(emptyMap())

    var detailsUser by mutableStateOf<UserAccount?>(null)

    var deactivateTarget by mutableStateOf<UserAccount?>(null)

    var profilesOpen by mutableStateOf(false)
        private set

    var permissionsOpen by mutableStateOf(false)
        private set

    var importOpen by mutableStateOf(false)
        private set

    var importing by mutableStateOf(false)
        private set

    var importResult by mutableStateOf<UserImportResult?>(null)
        private set

    var importError by mutableStateOf<String?>(null)
        private set

    val pageCount: Int
        get() = maxOf(1, totalPages)

    val paged: List<UserAccount>
        get() = users

    val isFormConfirmEnabled: Boolean
        get() =
            formFullName.isNotBlank() &&
                formEmail.isNotBlank() &&
                (formId != null || (formPassword.length >= 8 && formProfileId != null)) &&
                (formId == null || formEditPassword.isBlank() || formEditPassword.length >= 8)

    fun init() {
        if (initialized || initializing) return
        initializing = true
        scope.launch {
            loading = true
            try {
                refresh()
                initialized = true
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar os usuários."
            } finally {
                loading = false
                initializing = false
            }
        }
    }

    fun retry() {
        errorMessage = null
        if (!initialized) {
            init()
            return
        }
        scope.launch {
            loading = true
            try {
                refresh()
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar os usuários."
            } finally {
                loading = false
            }
        }
    }

    fun dismissError() {
        errorMessage = null
    }

    fun dismissSuccess() {
        successMessage = null
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
        requestPage(SEARCH_DEBOUNCE_MS)
    }

    fun updateOnlyActive(value: Boolean) {
        onlyActive = value
        page = 0
        requestPage()
    }

    fun updatePhone(value: String) {
        formPhone = formatPhone(value)
    }

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        requestPage()
    }

    private fun requestPage(delayMillis: Long = 0) {
        loadJob?.cancel()
        loadJob =
            scope.launch {
                if (delayMillis > 0) delay(delayMillis)
                loading = true
                errorMessage = null
                try {
                    val userPage = repository.loadUsersPage(page, pageSize, query, onlyActive)
                    users = userPage.users
                    totalPages = userPage.totalPages
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    errorMessage = throwable.message ?: "Não foi possível carregar os usuários."
                } finally {
                    loading = false
                }
            }
    }

    fun openAdd() {
        formMode = UserFormMode.Add
        formId = null
        formFullName = ""
        formCargo = ""
        formSector = ""
        formPhone = ""
        formEmail = ""
        formPassword = ""
        formEditPassword = ""
        formProfileId = null
        formPermissions = emptyMap()
    }

    fun openEdit(user: UserAccount) {
        formMode = UserFormMode.Edit
        formId = user.id
        formFullName = user.fullName
        formCargo = user.cargo
        formSector = user.sector
        formPhone = formatPhone(user.phone)
        formEmail = user.email
        formPassword = ""
        formEditPassword = ""
        formProfileId = user.profileId
        formPermissions = user.permissions
    }

    fun dismissForm() {
        formMode = null
    }

    fun selectProfile(profileId: String?) {
        formProfileId = profileId
        formPermissions = profiles.firstOrNull { it.id == profileId }?.permissions ?: emptyMap()
    }

    fun togglePermission(
        permissionId: String,
        action: PermissionAction,
    ) {
        val current = formPermissions[permissionId].orEmpty()
        val updated = if (action in current) current - action else current + action
        formPermissions =
            if (updated.isEmpty()) {
                formPermissions - permissionId
            } else {
                formPermissions + (permissionId to updated)
            }
    }

    fun submitForm() {
        val fullName = formFullName.trim()
        val email = formEmail.trim()
        if (fullName.isEmpty() || email.isEmpty()) return
        val successText = if (formId == null) "Usuário registrado com sucesso." else "Usuário editado com sucesso."
        scope.launch {
            runOperation(successMessage = successText) {
                val existing = users.firstOrNull { it.id == formId }
                val currentId = formId
                val user =
                    UserAccount(
                        id = existing?.id ?: currentId ?: generateId(),
                        fullName = fullName,
                        role = existing?.role ?: "",
                        cargo = formCargo.trim(),
                        companyNames = existing?.companyNames ?: emptyList(),
                        sector = formSector.trim(),
                        phone = formPhone.trim(),
                        email = email,
                        profileId = formProfileId,
                        permissions = formPermissions,
                        updatedBy = CURRENT_USER,
                        updatedAt = dateTimeProvider.nowTimestamp(),
                        lastAccess = existing?.lastAccess ?: "",
                        active = existing?.active ?: true,
                    )
                val userId =
                    if (currentId == null) {
                        repository.create(user, formPassword).also { formId = it }
                    } else {
                        repository.upsert(user)
                        if (formEditPassword.isNotBlank()) {
                            repository.changePassword(currentId, formEditPassword.trim())
                        }
                        formEditPassword = ""
                        currentId
                    }
                repository.replaceUserPermissions(userId, formPermissions)
                refresh()
                dismissForm()
            }
        }
    }

    fun openDetails(user: UserAccount) {
        detailsUser = user
    }

    fun dismissDetails() {
        detailsUser = null
    }

    fun requestDeactivate(user: UserAccount) {
        deactivateTarget = user
    }

    fun dismissDeactivate() {
        deactivateTarget = null
    }

    fun confirmDeactivate() {
        val target = deactivateTarget ?: return
        scope.launch {
            runOperation(successMessage = "Usuário inativado com sucesso.") {
                repository.changeStatus(target.id, active = false)
                refresh()
                deactivateTarget = null
            }
        }
    }

    fun activate(user: UserAccount) {
        scope.launch {
            runOperation(successMessage = "Usuário ativado com sucesso.") {
                repository.changeStatus(user.id, active = true)
                refresh()
            }
        }
    }

    fun openProfiles() {
        profilesOpen = true
    }

    fun dismissProfiles() {
        profilesOpen = false
    }

    fun saveProfile(profile: UserProfile) {
        scope.launch {
            runOperation {
                repository.upsertProfile(profile)
                refresh()
            }
        }
    }

    fun removeProfile(profileId: String) {
        scope.launch {
            runOperation {
                repository.removeProfile(profileId)
                refresh()
            }
        }
    }

    fun openImport() {
        importOpen = true
        importResult = null
        importError = null
    }

    fun dismissImport() {
        importOpen = false
        importResult = null
        importError = null
    }

    fun importUsers(
        profileId: String,
        rows: List<UserImportRow>,
    ) {
        if (importing) return
        importing = true
        importError = null
        importResult = null
        scope.launch {
            try {
                val result = repository.importUsers(profileId, rows)
                importResult = result
                refresh()
                successMessage = "${result.created} usuário(s) importado(s)."
                importOpen = false
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                importError = throwable.message ?: "Não foi possível importar os usuários."
            } finally {
                importing = false
            }
        }
    }

    suspend fun previewImport(rows: List<UserImportRow>): UserImportPreview = repository.previewImport(rows)

    fun openPermissions() {
        permissionsOpen = true
    }

    fun dismissPermissions() {
        permissionsOpen = false
    }

    fun savePermission(
        code: String,
        assignments: Map<String, Set<PermissionAction>>,
    ) {
        val id = normalizePermissionCode(code)
        if (id.isEmpty()) return
        scope.launch {
            runOperation {
                repository.upsertPermission(Permission(id = id))
                profiles
                    .filter { assignments[it.id]?.isNotEmpty() == true }
                    .forEach { profile ->
                        val actions = assignments.getValue(profile.id)
                        repository.upsertProfile(profile.copy(permissions = profile.permissions + (id to actions)))
                    }
                refresh()
            }
        }
    }

    fun renamePermission(
        id: String,
        code: String,
    ) {
        val newId = normalizePermissionCode(code)
        if (newId.isEmpty()) return
        val current = permissions.firstOrNull { it.id == id } ?: return
        if (current.id == newId) return
        scope.launch {
            runOperation {
                repository.removePermission(id)
                repository.upsertPermission(Permission(id = newId))
                refresh()
            }
        }
    }

    fun deletePermission(id: String) {
        scope.launch {
            runOperation {
                repository.removePermission(id)
                refresh()
            }
        }
    }

    private suspend fun refresh() {
        val userPage = repository.loadUsersPage(page, pageSize, query, onlyActive)
        users = userPage.users
        totalPages = userPage.totalPages
        profiles = repository.loadProfiles()
        permissions = repository.loadPermissions()
    }

    private suspend fun runOperation(
        successMessage: String = "Operação realizada com sucesso.",
        operation: suspend () -> Unit,
    ) {
        if (busy) return
        busy = true
        errorMessage = null
        this.successMessage = null
        try {
            operation()
            this.successMessage = successMessage
        } catch (throwable: Throwable) {
            errorMessage = throwable.message ?: "Não foi possível concluir a operação."
        } finally {
            busy = false
        }
    }

    private fun generateId(): String {
        var index = users.size + 1
        while (users.any { it.id == "us-novo-$index" }) {
            index++
        }
        return "us-novo-$index"
    }

    private companion object {
        const val CURRENT_USER = "Ana Souza"
        const val SEARCH_DEBOUNCE_MS = 300L
    }
}

@Composable
fun rememberUsersController(): UsersController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { UsersController(appGraph.userRepository, scope, appGraph.dateTimeProvider) }
}

private fun normalizePermissionCode(value: String): String =
    value
        .trim()
        .uppercase()
        .map(::asciiFold)
        .joinToString(separator = "")
        .replace(" ", "_")

private fun asciiFold(char: Char): Char =
    when (char) {
        'Á', 'À', 'Â', 'Ã', 'Ä' -> 'A'
        'É', 'È', 'Ê', 'Ë' -> 'E'
        'Í', 'Ì', 'Î', 'Ï' -> 'I'
        'Ó', 'Ò', 'Ô', 'Õ', 'Ö' -> 'O'
        'Ú', 'Ù', 'Û', 'Ü' -> 'U'
        'Ç' -> 'C'
        'Ñ' -> 'N'
        else -> char
    }

private fun formatPhone(value: String): String {
    val digits = value.filter(Char::isDigit).take(11)
    if (digits.isEmpty()) return ""
    if (digits.length <= 2) return "($digits"

    val area = digits.take(2)
    val number = digits.drop(2)
    val separator = if (number.length >= 8) "-" else ""
    val splitAt = if (number.length >= 8) number.length - 4 else number.length
    val formattedNumber = number.take(splitAt) + separator + number.drop(splitAt)
    return "($area) $formattedNumber"
}
